import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { createCircle } from '../services/circleService';

const ADMIN_UID = 'o2VVqpqGLvcPeXiNYe8FdGhXnBJ3';

export default function BulkCirclesPage() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [input, setInput] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [copiedMsgIndex, setCopiedMsgIndex] = useState(null);
  const [copiedType, setCopiedType] = useState(null);

  if (!currentUser || currentUser.uid !== ADMIN_UID) {
    navigate('/');
    return null;
  }

  const parseInput = (text) => {
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        const parts = line.split('|').map(p => p.trim());
        if (parts.length >= 2) {
          return { personName: parts[0], circleName: parts[1] };
        }
        const commaParts = line.split(',').map(p => p.trim());
        if (commaParts.length >= 2) {
          return { personName: commaParts[0], circleName: commaParts[1] };
        }
        return { personName: line, circleName: line };
      });
  };

  const generateMessage = (personName, shareLink) => {
    return `${personName} hope you are well. Here is your family link for Art Ping Pong. Just click to join and share with others for them to join you as well. Have fun and let me know what you think! ${shareLink}`;
  };

  const handleCreateAll = async () => {
    const entries = parseInput(input);
    if (entries.length === 0) {
      alert('Please enter at least one entry in the format: Person Name | Circle Name');
      return;
    }

    setLoading(true);
    const newResults = [];

    for (const entry of entries) {
      try {
        const circleId = await createCircle(currentUser.uid, entry.circleName, userProfile);
        
        const { db } = await import('../services/firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        
        const circleDoc = await getDoc(doc(db, 'artPingPongCircles', circleId));
        
        if (circleDoc.exists()) {
          const code = circleDoc.data().inviteCode;
          const shareLink = `${window.location.origin}/join/${code}`;
          const message = generateMessage(entry.personName, shareLink);
          
          newResults.push({
            personName: entry.personName,
            circleName: entry.circleName,
            inviteCode: code,
            shareLink,
            message
          });
        } else {
          throw new Error('Circle created but not found');
        }
      } catch (err) {
        console.error('Error creating circle:', err);
        newResults.push({
          personName: entry.personName,
          circleName: entry.circleName,
          inviteCode: 'ERROR',
          shareLink: 'Failed to create',
          message: 'Failed to create circle'
        });
      }
    }

    setResults(newResults);
    setLoading(false);
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
  };

  const copyCSV = async () => {
    const csv = [
      'Person Name,Circle Name,Invite Code,Share Link,Message',
      ...results.map(r => 
        `"${r.personName}","${r.circleName}","${r.inviteCode}","${r.shareLink}","${r.message}"`
      )
    ].join('\n');
    
    await copyToClipboard(csv);
    setCopiedType('csv');
    setTimeout(() => setCopiedType(null), 2000);
  };

  const copyAllLinks = async () => {
    const links = results.map(r => r.shareLink).join('\n');
    await copyToClipboard(links);
    setCopiedType('links');
    setTimeout(() => setCopiedType(null), 2000);
  };

  const copyLink = async (link, index) => {
    await copyToClipboard(link);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyMessage = async (message, index) => {
    await copyToClipboard(message);
    setCopiedMsgIndex(index);
    setTimeout(() => setCopiedMsgIndex(null), 2000);
  };

  const copyAllMessages = async () => {
    const messages = results.map(r => r.message).join('\n\n');
    await copyToClipboard(messages);
    setCopiedType('messages');
    setTimeout(() => setCopiedType(null), 2000);
  };

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate('/circles')}>
          <ArrowLeft size={20} />
        </BackBtn>
        <HeaderTitle>Bulk Create Circles</HeaderTitle>
        <Spacer />
      </Header>

      <Instructions>
        Enter one entry per line in the format: <Code>Person Name | Circle Name</Code>
        <Example>
          Example:<br />
          Sarah | Coy + Sarah<br />
          Mom | Coy + Mom<br />
          Dad | Coy + Dad
        </Example>
      </Instructions>

      <InputArea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Sarah | Coy + Sarah&#10;Mom | Coy + Mom&#10;Dad | Coy + Dad"
        rows={10}
      />

      <CreateButton onClick={handleCreateAll} disabled={loading || input.trim().length === 0}>
        {loading ? 'Creating...' : 'Create All'}
      </CreateButton>

      {results.length > 0 && (
        <>
          <ActionsRow>
            <ActionButton onClick={copyCSV}>
              {copiedType === 'csv' ? <Check size={18} /> : <Copy size={18} />}
              {copiedType === 'csv' ? 'Copied CSV!' : 'Copy CSV'}
            </ActionButton>
            <ActionButton onClick={copyAllLinks}>
              {copiedType === 'links' ? <Check size={18} /> : <Copy size={18} />}
              {copiedType === 'links' ? 'Copied Links!' : 'Copy All Links'}
            </ActionButton>
            <ActionButton onClick={copyAllMessages}>
              {copiedType === 'messages' ? <Check size={18} /> : <Copy size={18} />}
              {copiedType === 'messages' ? 'Copied!' : 'Copy All Messages'}
            </ActionButton>
          </ActionsRow>

          <ResultCards>
            {results.map((result, index) => (
              <ResultCard key={index}>
                <CardHeader>
                  <CardName>{result.personName}</CardName>
                  <CardCircle>{result.circleName}</CardCircle>
                  <CardCode><Code>{result.inviteCode}</Code></CardCode>
                </CardHeader>
                <CardLink>
                  <LinkText>{result.shareLink}</LinkText>
                  <CopyBtn onClick={() => copyLink(result.shareLink, index)}>
                    {copiedIndex === index ? <Check size={16} /> : <Copy size={16} />}
                  </CopyBtn>
                </CardLink>
                <CardMessage>
                  <MessageText>{result.message}</MessageText>
                  <CopyBtn onClick={() => copyMessage(result.message, index)}>
                    {copiedMsgIndex === index ? <Check size={16} /> : <Copy size={16} />}
                  </CopyBtn>
                </CardMessage>
              </ResultCard>
            ))}
          </ResultCards>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 0 16px 32px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  margin-bottom: 16px;
`;

const BackBtn = styled.button`
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  flex: 1;
`;

const Spacer = styled.div`
  width: 36px;
`;

const Instructions = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: ${({ theme }) => theme.radius.medium};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Example = styled.div`
  margin-top: 8px;
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Code = styled.code`
  padding: 2px 6px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 4px;
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 13px;
`;

const InputArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  font-size: 14px;
  font-family: 'SF Mono', 'Menlo', monospace;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const CreateButton = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 17px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    transform: translateY(-1px);
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  margin: 24px 0 16px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const ResultCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ResultCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CardName = styled.span`
  font-weight: 700;
  font-size: 15px;
`;

const CardCircle = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CardCode = styled.span`
  margin-left: auto;
`;

const CardLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CardMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
`;

const LinkText = styled.span`
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-all;
`;

const CopyBtn = styled.button`
  padding: 6px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const MessageText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.5;
  flex: 1;
`;
